/**
 * Optimize algorithm for the Military Training page (SF-70).
 * Pure function enabling clean isolated testing.
 *
 * Sequential priority order: OFFENSE_SPECIALIST → DEFENSE_SPECIALIST → ELITE → THIEF
 *
 * Key constraint: Thieves train from PEASANTS (not soldiers).
 * Source: TrainAction.java line 90: unitType == THIEF ? PEASANT : SOLDIER
 *
 * SF-179 Component 1: both `computeTrainOptimize` and `computeSingleUnitOptimize` previously
 * computed `deficit = Math.max(0, target - owned)`, never subtracting in-progress/queued training —
 * so Fill Strategy itself over-queued a second full batch on top of an already-in-flight one. Both
 * now route deficit math through the shared `unitStockMath.ts` (mirrors `buildingsPercent.ts`'s
 * `owned + inProgress` netting).
 */
import type { TrainableUnitType } from '../types/train';
import { computeUnitDeficit } from './unitStockMath';

export interface TrainOptimizeParams {
  armyComp: { offSpec: number; defSpec: number; elite: number };
  thiefPA: number;
  totalLand: number;
  owned: Record<TrainableUnitType, number>;
  inProgress: Record<TrainableUnitType, number>;
  costs: Record<TrainableUnitType, number>;   // cost per unit (caller applies ×2 for accelerated)
  goldBudget: number;
  soldierBudget: number;   // available soldiers (consumed by OFFENSE/DEFENSE/ELITE)
  peasantBudget: number;   // available peasants (consumed by THIEF only)
}

export interface TrainOptimizeResult {
  quantities: Record<string, number>;
  message: string;
}

const UNIT_ORDER: TrainableUnitType[] = [
  'OFFENSE_SPECIALIST',
  'DEFENSE_SPECIALIST',
  'ELITE',
  'THIEF',
];

/**
 * Computes recommended training quantities to reach strategy targets,
 * constrained by gold budget and soldier/peasant budgets.
 */
export function computeTrainOptimize(params: TrainOptimizeParams): TrainOptimizeResult {
  const { armyComp, thiefPA, totalLand, owned, inProgress, costs, goldBudget, soldierBudget, peasantBudget } = params;

  if (totalLand <= 0) {
    return { quantities: {}, message: 'No targets to fill' };
  }

  // Build target map: unit type → target count
  const targets: Record<TrainableUnitType, number> = {
    OFFENSE_SPECIALIST: Math.floor(armyComp.offSpec * totalLand),
    DEFENSE_SPECIALIST: Math.floor(armyComp.defSpec * totalLand),
    ELITE: Math.floor(armyComp.elite * totalLand),
    THIEF: Math.floor(thiefPA * totalLand),
  };

  let remainingGold = goldBudget;
  let remainingSoldiers = soldierBudget;
  let remainingPeasants = peasantBudget;
  const quantities: Record<string, number> = {};
  let totalFilled = 0;

  for (const unitType of UNIT_ORDER) {
    const target = targets[unitType];
    const ownedCount = owned[unitType] ?? 0;
    const inProgressCount = inProgress[unitType] ?? 0;
    const cost = costs[unitType] ?? 0;
    const { deficit } = computeUnitDeficit({ target, owned: ownedCount, inProgress: inProgressCount });

    if (deficit === 0 || cost <= 0) continue;

    const maxByGold = remainingGold > 0 ? Math.floor(remainingGold / cost) : 0;

    let fill: number;
    if (unitType === 'THIEF') {
      // Thieves train from peasants, not soldiers
      const maxByPeasants = remainingPeasants;
      fill = Math.min(deficit, maxByGold, maxByPeasants);
      remainingPeasants -= fill;
    } else {
      // OFFENSE/DEFENSE/ELITE train from soldiers
      const maxBySoldiers = remainingSoldiers;
      fill = Math.min(deficit, maxByGold, maxBySoldiers);
      remainingSoldiers -= fill;
    }

    if (fill > 0) {
      quantities[unitType] = fill;
      remainingGold -= fill * cost;
      totalFilled += fill;
    }
  }

  if (totalFilled === 0) {
    return { quantities: {}, message: 'No targets to fill' };
  }

  return {
    quantities,
    message: `Filled ${totalFilled} units to strategy targets`,
  };
}

export interface SingleUnitOptimizeParams {
  unitType: TrainableUnitType;
  target: number;
  ownedCount: number;
  /** Required, not optional — an omitted `inProgress` at a forgetful future call site must be a
   *  compile error, not a silent regression to the old over-queuing bug (Phase 3, resolves Phase 2
   *  frontend-dev C8 / qa-engineer D). */
  inProgress: number;
  cost: number;
  goldBudget: number;
  soldierBudget: number;
  peasantBudget: number;
}

/**
 * Computes the recommended quantity for a SINGLE unit type,
 * constrained only by gold and soldier/peasant budget for that unit.
 * Does not depend on other unit computations.
 *
 * SF-179: converted from 7 positional params to an object param (the same shape
 * `computeTrainOptimize` already uses) — a positional 8th param risked an argument-order mistake a
 * green suite wouldn't catch.
 */
export function computeSingleUnitOptimize(params: SingleUnitOptimizeParams): number {
  const { unitType, target, ownedCount, inProgress, cost, goldBudget, soldierBudget, peasantBudget } = params;
  if (cost <= 0) return 0;

  const { deficit } = computeUnitDeficit({ target, owned: ownedCount, inProgress });
  if (deficit === 0) return 0;

  const maxByGold = goldBudget > 0 ? Math.floor(goldBudget / cost) : 0;

  if (unitType === 'THIEF') {
    return Math.min(deficit, maxByGold, peasantBudget);
  }
  return Math.min(deficit, maxByGold, soldierBudget);
}

/**
 * SF-179 Component 1 idempotency fix (nice-to-have, adopted): `computeTrainOptimize`'s merge
 * behavior previously spread `prev` and only overwrote unit types with a nonzero fresh deficit —
 * leaving a stale typed quantity on a row the deficit fix now correctly recognizes as "at target"
 * (e.g. after a first Fill Strategy click trains some units, a second click's now-empty deficit for
 * that type left the first click's drafted number sitting in the input). Writes an explicit `0` for
 * every trainable unit type not present in `quantities`, so two consecutive Fill Strategy clicks
 * (pre-submit) produce an identical result.
 */
export function mergeTrainQuantities(
  prevQty: Record<string, number>,
  quantities: Record<string, number>,
  unitTypes: readonly TrainableUnitType[],
): Record<string, number> {
  const next = { ...prevQty };
  for (const unitType of unitTypes) {
    next[unitType] = quantities[unitType] ?? 0;
  }
  return next;
}
